import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { payrollAdminPackageApi } from "../../services/payrollAdminPackageApi";
import { payrollApi } from "../../services/payrollApi";
import { usePermissions } from "../../hooks/usePermissions";
import { formatApiErrorDetail } from "../../lib/api";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useLocalization } from "../../hooks/useLocalization";

export default function PayrollAdminPackagePage() {
  const { periodId } = useParams();
  const { t } = useLocalization();
  const { canManagePayrollPackage } = usePermissions();
  const [period, setPeriod] = useState(null);
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [options, setOptions] = useState({ report_type: "detailed", sort_by: "employee_name", confidential_marking: true, duplex_mode: true, include_timecard_images: false });

  const load = useCallback(() => {
    payrollApi.getPeriod(periodId).then((r) => setPeriod(r.data)).catch(() => {});
    payrollAdminPackageApi.validate(periodId).then((r) => setValidation(r.data)).catch(() => {});
  }, [periodId]);

  useEffect(() => { if (canManagePayrollPackage) load(); }, [canManagePayrollPackage, load]);

  if (!canManagePayrollPackage) return <p className="text-sm text-gray-500" data-testid="admin-package-access-denied">{t("common.accessDenied")}</p>;

  const generate = async () => {
    setBusy(true);
    try {
      await payrollAdminPackageApi.generate(periodId, options);
      toast.success(t("mgmtPayroll.adminPackageGenerated"));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail?.blockers?.join("; ") || e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-5" data-testid="admin-package-page">
      <PageHelpBanner pageKey="payroll-admin-package" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtPayroll.adminPackageHelp")}</p>
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtPayroll.adminPackage")} - {period?.payroll_month}</h2>
        <p className="text-sm text-gray-500">{t("mgmtPayroll.adminPackageSubtitle")}</p>
      </div>

      {validation && (
        <div className={`rounded-xl border p-4 ${validation.blockers.length ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="admin-package-validation-panel">
          <p className="text-sm font-semibold mb-2">{validation.blockers.length ? t("mgmtPayroll.blockedCannotFinalize") : t("mgmtPayroll.readyToFinalize")}</p>
          <ul className="list-disc pl-5 text-sm space-y-1">
            {validation.blockers.map((b, i) => <li key={i} data-testid={`admin-package-blocker-${i}`} className="text-red-700">{b}</li>)}
          </ul>
          {!validation.blockers.length && <p className="text-sm text-emerald-700">{validation.employee_count} {t("mgmtPayroll.employeesWillBeIncluded")}</p>}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="admin-package-options-panel">
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600 w-24">{t("mgmtPayroll.reportType")}</label>
          <Select value={options.report_type} onValueChange={(v) => setOptions({ ...options, report_type: v })}>
            <SelectTrigger data-testid="admin-package-report-type-select" className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="summary">{t("mgmtPayroll.summary1Page")}</SelectItem>
              <SelectItem value="detailed">{t("mgmtPayroll.detailed2Page")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600 w-24">{t("mgmtPayroll.sortBy")}</label>
          <Select value={options.sort_by} onValueChange={(v) => setOptions({ ...options, sort_by: v })}>
            <SelectTrigger data-testid="admin-package-sort-select" className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="employee_name">{t("mgmtPayroll.employeeName")}</SelectItem>
              <SelectItem value="employee_id">{t("mgmtPayroll.employeeId")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600"><Checkbox data-testid="admin-package-duplex-checkbox" checked={options.duplex_mode} onCheckedChange={(v) => setOptions({ ...options, duplex_mode: !!v })} /> {t("mgmtPayroll.duplexPrintInstruction")}</label>
        <label className="flex items-center gap-2 text-sm text-gray-600"><Checkbox data-testid="admin-package-confidential-checkbox" checked={options.confidential_marking} onCheckedChange={(v) => setOptions({ ...options, confidential_marking: !!v })} /> {t("mgmtPayroll.confidentialMarking")}</label>
        <label className="flex items-center gap-2 text-sm text-gray-600"><Checkbox data-testid="admin-package-include-images-checkbox" checked={options.include_timecard_images} onCheckedChange={(v) => setOptions({ ...options, include_timecard_images: !!v })} /> {t("mgmtPayroll.includeRawScans")}</label>

        <div className="flex gap-2 pt-2">
          <Button data-testid="generate-admin-package-btn" disabled={busy || (validation && validation.blockers.length > 0)} onClick={generate}>{t("mgmtPayroll.finalizePackage")}</Button>
          {period?.admin_package_path && (
            <Button variant="outline" asChild><a data-testid="download-admin-package-link" href={payrollAdminPackageApi.getDownloadUrl(periodId)} target="_blank" rel="noreferrer">{t("mgmtPayroll.downloadZip")}</a></Button>
          )}
        </div>
      </div>
    </div>
  );
}

